import {useEffect} from 'react';
import {useAppDispatch, useAppSelector} from '../../../hooks/redux';
import socketService from '../../../middleware/websocketMiddleware';
import {fetchBillingStatus} from '../model/actions';
import {
    openBillingModal,
    setBillingConnected,
} from '../model/slice';
import {
    selectBillingSignature,
    selectBillingStatus,
    selectBillingModalOpen,
} from '../model/selectors';
import {selectCurrentCompany} from '../../companies/model/selectors';
import {selectIsAuth} from "../../auth/model/selectors";

const POLL_INTERVAL_MS = 5 * 60 * 1000;

function seenKey(companyId: string, signature: string): string {
    return `billing-seen:${companyId}:${signature}`;
}

export function useBillingTracking() {
    const dispatch = useAppDispatch();
    const company = useAppSelector(selectCurrentCompany);
    const companyId = company?.id ?? null;
    const isAuth = useAppSelector(selectIsAuth);

    // WebSocket-подписка + fallback polling
    useEffect(() => {
        if (!isAuth) return;
        const socket = socketService.connect();

        const handleConnect = () => {
            socket.emit('billing:join');
            dispatch(setBillingConnected(true));
            dispatch(fetchBillingStatus());
        };

        const handleStatusChanged = () => {
            dispatch(fetchBillingStatus());
        };

        const handleDisconnect = () => {
            dispatch(setBillingConnected(false));
        };

        socket.on('connect', handleConnect);
        socket.on('billing:status_changed', handleStatusChanged);
        socket.on('disconnect', handleDisconnect);

        if (socket.connected) handleConnect();

        const pollId = window.setInterval(() => {
            dispatch(fetchBillingStatus());
        }, POLL_INTERVAL_MS);

        return () => {
            window.clearInterval(pollId);
            socket.emit('billing:leave');
            socket.off('connect', handleConnect);
            socket.off('billing:status_changed', handleStatusChanged);
            socket.off('disconnect', handleDisconnect);
            dispatch(setBillingConnected(false));
        };
    }, [dispatch, isAuth]);

    // Дедупликация показа модалки через localStorage
    const signature = useAppSelector(selectBillingSignature);
    const status = useAppSelector(selectBillingStatus);
    const modalOpen = useAppSelector(selectBillingModalOpen);

    useEffect(() => {
        if (!signature || !companyId) return;
        if (status === 'active' || status === 'unknown') return;
        if (modalOpen) return;

        const key = seenKey(companyId, signature);
        if (localStorage.getItem(key)) return;

        localStorage.setItem(key, String(Date.now()));
        dispatch(openBillingModal());
    }, [signature, status, companyId, modalOpen, dispatch]);
}